import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import useNetworkStatus from '../hooks/useNetworkStatus';
import ProfileModal from './profile/ProfileModal';
import InstallPWA from './InstallPWA';
import { isBrokerPersona } from '../constants/personas';
import { canAccessAdminShell } from '../constants/adminRoles';

const Header = ({ onNavigateToClients, onNavigateToChecklists, onNavigateToMarketParams, onNavigateToHome, onNavigateToMyFiles }) => {
  const { currentUser, userProfile, logout, updateUserProfile, adminRole, impersonation } = useAuth();
  const { t } = useLanguage();
  const isOnline = useNetworkStatus();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileNavRef = useRef(null);

  const displayName =
    userProfile?.displayName ?? currentUser?.displayName ?? currentUser?.email ?? '';
  const avatarUrl = userProfile?.avatarUrl ?? currentUser?.photoURL;
  const availability =
    userProfile?.availability === 'away'
      ? 'away'
      : userProfile?.availability === 'offline'
        ? 'offline'
        : 'online';

  const isBroker = isBrokerPersona(userProfile?.persona);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (mobileNavRef.current && !mobileNavRef.current.contains(e.target)) {
        setMobileNavOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const openProfileForTour = () => {
      setProfileModalOpen(true);
      setDropdownOpen(false);
      setMobileNavOpen(false);
    };
    window.addEventListener('rentalyzer:onboarding-open-profile', openProfileForTour);
    return () => window.removeEventListener('rentalyzer:onboarding-open-profile', openProfileForTour);
  }, []);

  if (!currentUser) return null;

  const navItems = (
    <>
      {onNavigateToMyFiles && (
        <button
          type="button"
          onClick={() => {
            onNavigateToMyFiles();
            setDropdownOpen(false);
            setMobileNavOpen(false);
          }}
          className="text-sm text-gray-700 hover:text-blue-600 px-2 py-1.5 rounded text-left w-full md:w-auto"
        >
          {t('myFiles.title')}
        </button>
      )}
      {isBroker && (
        <>
          <button
            type="button"
            onClick={() => {
              onNavigateToClients?.();
              setDropdownOpen(false);
              setMobileNavOpen(false);
            }}
            className="text-sm text-gray-700 hover:text-blue-600 px-2 py-1.5 rounded text-left w-full md:w-auto"
          >
            {userProfile?.persona === 'preteur_prive' ? t('home.borrowers') : t('home.clients')}
          </button>
          {onNavigateToChecklists && (
            <button
              type="button"
              onClick={() => {
                onNavigateToChecklists?.();
                setDropdownOpen(false);
                setMobileNavOpen(false);
              }}
              className="text-sm text-gray-700 hover:text-blue-600 px-2 py-1.5 rounded text-left w-full md:w-auto"
            >
              {t('home.checklists')}
            </button>
          )}
          {onNavigateToMarketParams && (
            <button
              type="button"
              onClick={() => {
                onNavigateToMarketParams();
                setDropdownOpen(false);
                setMobileNavOpen(false);
              }}
              className="text-sm text-gray-700 hover:text-blue-600 px-2 py-1.5 rounded text-left w-full md:w-auto"
            >
              {t('home.marketParams')}
            </button>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      <header className="p-3 sm:p-4 flex justify-between items-center gap-2 bg-white shadow relative z-30">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            type="button"
            data-onboarding="header-home"
            onClick={() => onNavigateToHome?.()}
            className="flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500/50 rounded shrink-0"
            aria-label={t('header.goToHome')}
          >
            <img
              src="/rentalyzer-logo.png"
              alt="Rentalyzer logo"
              className="h-9 sm:h-12"
            />
          </button>
          <div className="relative md:hidden shrink-0" ref={mobileNavRef}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMobileNavOpen((o) => !o);
                setDropdownOpen(false);
              }}
              className="p-2 rounded-lg text-gray-700 hover:bg-gray-100"
              aria-expanded={mobileNavOpen}
              aria-label={t('header.menu') || 'Menu'}
            >
              {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            {mobileNavOpen && (
              <div className="absolute left-0 top-full mt-1 w-52 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-40 flex flex-col">
                <div data-onboarding="header-nav" className="flex flex-col px-1">
                  {navItems}
                </div>
              </div>
            )}
          </div>
          <nav className="hidden md:flex gap-2 items-center" data-onboarding="header-nav">
            {navItems}
          </nav>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              data-onboarding="header-profile"
              onClick={() => {
                setDropdownOpen((o) => !o);
                setMobileNavOpen(false);
              }}
              className="flex items-center gap-1.5 sm:gap-2 rounded-lg hover:bg-gray-100 px-1.5 sm:px-2 py-1.5 transition-colors"
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover shrink-0"
                />
              ) : (
                <span className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-medium shrink-0">
                  {(displayName || '?').charAt(0).toUpperCase()}
                </span>
              )}
              <span className="hidden sm:inline text-sm text-gray-700 max-w-[100px] lg:max-w-[140px] truncate">
                {displayName || t('profile.unknown')}
              </span>
              <span
                className={`hidden lg:inline ml-1 px-2 py-0.5 rounded text-xs font-medium text-white shrink-0 ${
                  availability === 'online'
                    ? 'bg-green-600'
                    : availability === 'away'
                      ? 'bg-amber-500'
                      : 'bg-slate-500'
                }`}
                title={t('availability.menuTitle')}
              >
                {availability === 'online'
                  ? t('availability.online')
                  : availability === 'away'
                    ? t('availability.away')
                    : t('availability.offline')}
              </span>
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${isOnline ? 'bg-emerald-500' : 'bg-red-500'}`}
                title={isOnline ? t('connectivity.online') : t('connectivity.offline')}
                aria-hidden
              />
            </button>
            <div
              className={`absolute right-0 top-full mt-1 w-52 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-40 ${
                dropdownOpen ? '' : 'hidden'
              }`}
            >
              <div className="px-3 py-2 border-b border-gray-100">
                <p className="text-xs font-medium text-gray-500 mb-1.5">{t('availability.menuTitle')}</p>
                <div className="flex flex-col gap-0.5">
                  {(['online', 'away', 'offline']).map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={async () => {
                        try {
                          await updateUserProfile({
                            availability: key,
                            availabilityUpdatedAt: new Date(),
                          });
                        } catch (e) {
                          console.warn(e);
                        }
                        setDropdownOpen(false);
                      }}
                      className={`text-left text-sm px-2 py-1.5 rounded ${
                        availability === key ? 'bg-indigo-50 text-indigo-800 font-medium' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {key === 'online'
                        ? t('availability.online')
                        : key === 'away'
                          ? t('availability.away')
                          : t('availability.offline')}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setProfileModalOpen(true);
                  setDropdownOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t('profile.menuItem')}
              </button>
              {canAccessAdminShell(adminRole) && !impersonation?.active ? (
                <Link
                  to="/admin"
                  onClick={() => setDropdownOpen(false)}
                  className="block w-full text-left px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50"
                >
                  {t('admin.shell.menuItem')}
                </Link>
              ) : null}
              <InstallPWA
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onPrompt={() => setDropdownOpen(false)}
              />
              <button
                type="button"
                onClick={() => {
                  setDropdownOpen(false);
                  logout();
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>
      {profileModalOpen && (
        <ProfileModal onClose={() => setProfileModalOpen(false)} />
      )}
    </>
  );
};

export default Header;
